// app/providers.tsx
"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, Suspense, useRef, useState } from "react";
import { usePostHog } from "posthog-js/react";
import { useSession } from "next-auth/react";

import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createQueryClient, setupQueryPersistence } from "@/lib/queryClient";
import { clientLogger } from "@/lib/logger-client";

// Silence client-side console logging in production
if (typeof window !== "undefined" && process.env.NODE_ENV === "production") {
  const noop = () => {};
  window.console.log = noop;
  window.console.debug = noop;
  window.console.info = noop;
  window.console.warn = noop;
  window.console.error = noop;
  window.console.group = noop;
  window.console.groupEnd = noop;
  window.console.table = noop;
  window.console.time = noop;
  window.console.timeEnd = noop;
}

// Component to handle PostHog user identification
function PostHogIdentifier() {
  const { data: session, status } = useSession();
  const posthog = usePostHog();
  const hasIdentified = useRef(false);
  const lastUserId = useRef<string | null>(null);

  useEffect(() => {
    if (!posthog) return;

    // Handle user identification when session becomes authenticated
    if (status === "authenticated" && session?.user) {
      const userId = session.user.id;
      const userEmail = session.user.email;

      // Only identify if we have email and haven't already identified this user
      if (userEmail && (!hasIdentified.current || lastUserId.current !== userId)) {
        console.log("PostHog: Identifying user", { userId, email: userEmail });
        
        // Identify the user with PostHog
        posthog.identify(userId, {
          email: userEmail,
          user_id: userId,
          name: session.user.name,
          image: session.user.image,
        });

        hasIdentified.current = true;
        lastUserId.current = userId;
      }
    }
    
    // Handle sign out - reset PostHog
    if (status === "unauthenticated" && hasIdentified.current) {
      console.log("PostHog: Resetting user session");
      posthog.reset();
      hasIdentified.current = false;
      lastUserId.current = null;
    }
  }, [session, status, posthog]);

  return null;
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
      // Route all PostHog traffic through our own domain to avoid ad blockers.
      // /hc-events is a reverse proxy defined in next.config.mts rewrites().
      // ui_host must still point to PostHog's real domain so toolbar links work.
      api_host: '/hc-events',
      ui_host: 'https://us.posthog.com',
      person_profiles: "always", // or 'always' to create profiles for anonymous users as well
      capture_pageview: false, // PostHog's automatic capture only fires on full page loads, missing all client-side route changes. We capture manually in PostHogPageView using usePathname/useSearchParams to catch every Next.js navigation.
      capture_pageleave: true, // Capture pageleave events
      capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
        capture_console_errors: true,
      },
    });

    posthog.register({ source: 'web'});

    // Store PostHog session ID in localStorage so Chrome extension can access it
    // This allows extension errors and events to be linked to the same session as app events
    try {
      const sessionId = posthog.get_session_id?.();
      if (sessionId) {
        localStorage.setItem('posthog_session_id', sessionId);
        console.log('[PostHog] Stored session ID in localStorage:', sessionId);
      }
    } catch (error) {
      clientLogger.warn('[providers] Failed to store PostHog session ID in localStorage:', { error });
    }
  }, []);

  // Global listener to catch Next.js ChunkLoadErrors (which happen when a new deployment
  // deletes older compiled chunks) and trigger a full page reload to get fresh assets.
  useEffect(() => {
    const handleChunkError = (event: ErrorEvent | PromiseRejectionEvent) => {
      let errorMsg = "";
      if ('message' in event) {
        errorMsg = event.message || "";
      } else if (event.reason) {
        errorMsg = event.reason.message || String(event.reason);
      }

      if (
        errorMsg.includes("Loading chunk") ||
        errorMsg.includes("ChunkLoadError")
      ) {
        clientLogger.warn("Next.js ChunkLoadError detected, forcing page reload to retrieve latest build assets.", {
          error: 'reason' in event ? event.reason : event.error
        });
        window.location.reload();
      }
    };

    window.addEventListener("error", handleChunkError);
    window.addEventListener("unhandledrejection", handleChunkError);
    return () => {
      window.removeEventListener("error", handleChunkError);
      window.removeEventListener("unhandledrejection", handleChunkError);
    };
  }, []);

  return (
    <PHProvider client={posthog}>
      <SuspendedPostHogPageView />
      <PostHogIdentifier />
      <QueryProvider>
        {children}
      </QueryProvider>
    </PHProvider>
  );
}

/**
 * Provides a stable QueryClient instance for the entire app. Persistence to
 * localStorage is keyed by userId so cached queries don't leak between users.
 */
function QueryProvider({ children }: { children: React.ReactNode }) {
  const { data: session } = useSession();
  const userId = session?.user?.id;

  const [queryClient] = useState<QueryClient>(() => createQueryClient());

  // Wire up localStorage persistence once we have a userId
  useEffect(() => {
    if (!userId) return;
    const unsubscribe = setupQueryPersistence(queryClient, userId);
    return unsubscribe;
  }, [queryClient, userId]);

  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
}

function PostHogPageView() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const posthog = usePostHog();

  // Track pageviews and keep session cookie fresh for server-side correlation
  useEffect(() => {
    if (pathname && posthog) {
      let url = window.origin + pathname;
      if (searchParams.toString()) {
        url = url + "?" + searchParams.toString();
      }

      posthog.capture("$pageview", { $current_url: url });

      // Keep the ph_session cookie up-to-date so server-side routes can read the
      // current PostHog session ID for error correlation. Updated on every navigation
      // so it stays current if PostHog starts a new session after inactivity.
      // This replaces __add_tracing_headers (which caused recursive fetch failures).
      try {
        const sessionId = posthog.get_session_id?.();
        if (sessionId) {
          document.cookie = `ph_session=${sessionId}; path=/; SameSite=Strict`;
          localStorage.setItem('posthog_session_id', sessionId);
        }
      } catch {
        // Non-critical, ignore
      }
    }
  }, [pathname, searchParams, posthog]);

  return null;
}

// Component to sync user created_at to PostHog person properties.
// Must be rendered inside DataProvider (authenticated routes only).
export function PostHogCreatedAtSync({ createdAt }: { createdAt: Date | null }) {
  const posthog = usePostHog();
  const hasSynced = useRef(false);

  useEffect(() => {
    if (!posthog || !createdAt || hasSynced.current) return;
    posthog.setPersonProperties({ $created_at: createdAt.toISOString() });
    hasSynced.current = true;
  }, [posthog, createdAt]);

  return null;
}

// Wrap PostHogPageView in Suspense to avoid the useSearchParams usage above
// from de-opting the whole app into client-side rendering
// See: https://nextjs.org/docs/messages/deopted-into-client-rendering
function SuspendedPostHogPageView() {
  return (
    <Suspense fallback={null}>
      <PostHogPageView />
    </Suspense>
  );
}
